// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useState } from 'react';
import block from 'propmods';
import { Redirect, Route, Switch as ReactRouterSwitch } from 'react-router';
import { useI18N } from '@yandex-int/i18n/lib/react';
import * as keyset from 'i18n/Settings';
import { PageAside, PageContent, PageHeadline, PageMain } from '../Typo/Typo';
import { isPrivateThereminvoxSkill } from '../../utils/thereminvox';
import Switch from '../Switch/Switch';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import Menu from '../Menu/Menu';
import { routes } from '../../lib/routes';
import Settings from '../Settings/Settings';
import { Stuck } from '../Stuck/Stuck';
import { DraftControlPanel } from '../DraftControlPanel/DraftControlPanel';
import { useSettingsPageMenuItems } from '../../hooks/useSettingsPageMenuItems';
import { DeviceLinkingSection } from '../DeviceLinkingSection/DeviceLinkingSection';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { getSettingsEntity } from '../../utils';
import { GrammarPage } from '../GrammarPage/GrammarPage';
import { isInDevelopment } from '../../model/draft';
import { SettingsPageContext } from '../../context/settingsPageContext';
import { UserAgreements } from '../UserAgreements/UserAgreements';

import './SettingsPage.scss';
import Banners from '../Banners/Banners';

const b = block('SettingsPage');

interface Props {
    onTypeChange: (_isDraft: boolean) => void;
    isDraft: boolean;
}

export const SettingsPage: React.FC<Props> = ({ onTypeChange, isDraft }) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const skill = useCurrentAliceApp()!;
    const [hasChanges, setHasChanges] = useState(false);
    const t = useI18N(keyset);

    const { menuItems, display } = useSettingsPageMenuItems(skill, isDraft);

    const isMultipleSettingsTabs = menuItems.length > 1;

    const draftModifier = isDraft ? '_draft' : '';
    const draftStatusModifier = `_status_${skill.draft.status}`;

    const settings = getSettingsEntity(skill, isDraft);

    return (
        <ErrorBoundary withHomepageLink>
            <SettingsPageContext.Provider value={{ isDraft, isEditable: isDraft && isInDevelopment(skill) }}>
                <div {...b()}>
                    <PageHeadline
                        title={t('settings')}
                        subtitle={
                            isPrivateThereminvoxSkill(skill.draft) ? null : (
                                <Switch
                                    items={[
                                        { value: 'draft', text: t('draft') },
                                        {
                                            value: 'production',
                                            text: t('published'),
                                            title: skill.onAir ? undefined : t('no-published'),
                                            disabled: !skill.onAir,
                                        },
                                    ]}
                                    value={isDraft ? 'draft' : 'production'}
                                    onSwitch={(value) => onTypeChange(value === 'draft')}
                                />
                            )
                        }
                    />
                    {isMultipleSettingsTabs && (
                        <div {...b('nav')}>
                            <Stuck
                                render={(isStuck) => (
                                    <div {...b('nav-menu-container', { stuck: isStuck })}>
                                        <div {...b('nav-menu')}>
                                            <Menu items={menuItems} />
                                        </div>
                                    </div>
                                )}
                                when={(rect) => rect.top <= 0}
                            />
                        </div>
                    )}
                    <PageContent key={`SettingsPage${draftModifier}${draftStatusModifier}`}>
                        <PageMain>
                            <ReactRouterSwitch>
                                {display.main && (
                                    <Route path={routes.app.skillSettings(isDraft, 'main', skill.id)}>
                                        {() => (
                                            <Settings
                                                aliceApp={skill}
                                                isDraft={isDraft}
                                                registerChanges={setHasChanges}
                                                hasChanges={hasChanges}
                                                settings={settings}
                                            />
                                        )}
                                    </Route>
                                )}
                                {display.devices && (
                                    <Route path={routes.app.skillSettings(isDraft, 'devices', skill.id)}>
                                        <DeviceLinkingSection />
                                    </Route>
                                )}
                                {display.intents && (
                                    <Route path={routes.app.skillSettings(isDraft, 'intents', skill.id)}>
                                        <GrammarPage />
                                    </Route>
                                )}
                                {display.userAgreements && (
                                    <Route path={routes.app.skillSettings(isDraft, 'userAgreements', skill.id)}>
                                        <UserAgreements />
                                    </Route>
                                )}
                                <Redirect
                                    exact
                                    from={routes.app.skillPage(isDraft, 'settings', skill.id)}
                                    to={routes.app.skillSettings(Boolean(isDraft), 'main', skill.id)}
                                />
                            </ReactRouterSwitch>
                        </PageMain>
                        {isDraft && (
                            <div {...b('draft-control-panel', { 'offset-top': isMultipleSettingsTabs })}>
                                <PageAside>
                                    {skill.isBanned ? (
                                        t('dialog-banned')
                                    ) : (
                                        <DraftControlPanel
                                            settingsAreValid={skill.draft.isAllowedForDeploy}
                                            safeToRequestReview={!hasChanges}
                                            afterReviewRequest={() => setHasChanges(false)}
                                        />
                                    )}
                                    <Banners />
                                </PageAside>
                            </div>
                        )}
                    </PageContent>
                </div>
            </SettingsPageContext.Provider>
        </ErrorBoundary>
    );
};
